import MenuItem from "@mui/material/MenuItem";
import { CircularProgress, IconButton, Stack } from "@mui/material";
import React, { useMemo, useState } from "react";
import { MoreVertIcon } from "../images/MoreVertIcon";
import { StyledMenu } from "./StyledMenu";
import { useNavigate, useParams } from "react-router-dom";
import { EyeIcon } from "../images/EyeIcon";
import { ExportDataIcon, StarsOutlineIcon, TrashIcon } from "../images";
import { ActivitiesStatus, AppRoutes } from "../../types/enums";
import {
  campaignsApi,
  useGeneratePlaybookForMembersMutation,
  useGeneratePlaybookPDFMutation,
  useLazyGetPlaybookQuery,
  useRemoveMembersMutation,
} from "../../api";
import { toast } from "react-toastify";
import { CustomToast } from "./CustomToast";
import { getErrorMessage } from "../../utils/getErrorMessage";
import {
  setIsNotificationsOpen,
  setRemovedId,
} from "../../features/companySlice";
import { useAppDispatch } from "../../hooks";

export const MemberMenu = ({
  member_id,
  isDisabled,
  playbookStatus = ActivitiesStatus.queued,
}: {
  member_id: string;
  isDisabled: boolean;
  playbookStatus?: ActivitiesStatus;
}) => {
  const { campaign_id, account_id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [removeMembers, { isLoading }] = useRemoveMembersMutation();
  const [generatePlaybookForMembers, { isLoading: isGeneratingPlaybooks }] =
    useGeneratePlaybookForMembersMutation();
  const [getPlaybook, { isFetching: isPlaybookLoading }] =
    useLazyGetPlaybookQuery();
  const [generatePlaybookPDF, { isLoading: isGeneratingPlaybookPDF }] =
    useGeneratePlaybookPDFMutation();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const exportPlaybook = () => {
    getPlaybook({ member_id })
      .unwrap()
      .then((topics) => {
        generatePlaybookPDF({ member_id, topics })
          .unwrap()
          .then((blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `BDR Engagement Playbook for ${topics.lead_company} - ${topics.lead_name}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            handleClose();
          })
          .catch((err) => {
            toast.error(
              <CustomToast
                type={"error"}
                text={
                  getErrorMessage(err).globalError ||
                  "Something went wrong. Please try again"
                }
                title={"Failed to generate BDR Engagement Playbook PDF"}
              />,
            );
          });
      })
      .catch((err) =>
        toast.error(
          <CustomToast
            type="error"
            title="Something went wrong"
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
          />,
        ),
      );
  };

  const removeMemberHandler = () => {
    if (!campaign_id || !account_id) return null;
    removeMembers({ campaign_id, account_id, member_ids: [member_id] })
      .unwrap()
      .then(() => {
        handleClose();
        dispatch(setRemovedId(member_id));
        dispatch(campaignsApi.util.invalidateTags(["role-members", "company"]));
      })
      .catch((err) =>
        toast.error(
          <CustomToast
            type="error"
            title="Something went wrong"
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
          />,
        ),
      );
  };

  const generatePlaybooksHandler = () => {
    if (campaign_id) {
      generatePlaybookForMembers({
        campaign_id,
        ids: [member_id],
        include_all: false,
      })
        .unwrap()
        .then(() => {
          dispatch(setIsNotificationsOpen(true));
          handleClose();
        })
        .catch((err) =>
          toast.error(
            <CustomToast
              type="error"
              text={
                getErrorMessage(err).globalError ||
                "Something went wrong. Please try again"
              }
              title="Playbook generation failed."
            />,
          ),
        );
    }
  };

  const list = useMemo(
    () => [
      {
        label: "Generate Playbook",
        icon: <StarsOutlineIcon color={"#1D3E70"} />,
        handler: generatePlaybooksHandler,
        isLoading:
          playbookStatus === ActivitiesStatus.in_progress ||
          isGeneratingPlaybooks,
        isDisabled:
          playbookStatus === ActivitiesStatus.in_progress ||
          isGeneratingPlaybooks,
        isHide: playbookStatus === ActivitiesStatus.completed,
      },
      {
        label: "View Playbook",
        icon: <EyeIcon color={"#1D3E70"} />,
        handler: () =>
          navigate(`/${campaign_id}${AppRoutes.PLAYBOOK}/${member_id}`),
        isLoading: false,
        isDisabled: false,
        isHide: playbookStatus !== ActivitiesStatus.completed,
      },
      {
        label: "Export Playbook (PDF)",
        icon: <ExportDataIcon />,
        handler: exportPlaybook,
        isLoading: isGeneratingPlaybookPDF || isPlaybookLoading,
        isDisabled: isGeneratingPlaybookPDF || isPlaybookLoading,
        isHide: playbookStatus !== ActivitiesStatus.completed,
      },
      {
        label: "Remove contact",
        icon: <TrashIcon />,
        handler: removeMemberHandler,
        isLoading: isLoading,
        isDisabled: isLoading,
        isHide: false,
      },
    ],
    [
      playbookStatus,
      isLoading,
      isGeneratingPlaybooks,
      isPlaybookLoading,
      isGeneratingPlaybookPDF,
    ],
  );

  return (
    <Stack
      style={{
        width: "48px",
        minWidth: "48px",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <IconButton
        disabled={isDisabled}
        aria-label="more"
        id="long-button"
        aria-controls={open ? "long-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MoreVertIcon width="24" />
      </IconButton>
      <StyledMenu
        id="demo-customized-menu"
        MenuListProps={{
          "aria-labelledby": "demo-customized-button",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: "white",
          },
        }}
      >
        {list.map(({ label, handler, icon, isDisabled, isLoading, isHide }) => {
          if (isHide) return null;

          return (
            <MenuItem
              key={label}
              disabled={isLoading || isDisabled}
              sx={{
                display: "flex",
                gap: "12px",
                alignItems: "center",
                color: "#1D3E70",
              }}
              onClick={handler}
            >
              {isLoading ? (
                <CircularProgress size={24} sx={{ color: "inherit" }} />
              ) : (
                icon
              )}
              {label}
            </MenuItem>
          );
        })}
      </StyledMenu>
    </Stack>
  );
};
